'use client';

import { type ReactNode, useState } from 'react';
import { ChevronDown, ListChecks } from 'lucide-react';
import { cn } from '@tinct/core-utils';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@tinct/web-components';
import { NoticeAlert } from '../notice-alert';

export interface ContextSummaryAction {
  label: string;
  variant?: 'default' | 'outline';
  icon?: ReactNode;
  onClick: () => void;
}

interface ContextSummaryContentProps {
  title: string;
  /**
   * Labelled entries, or a plain multi-line text (rendered as a list, with lines ending in
   * `:` as section headings and `- ` lines as bullets), or null.
   */
  data: Record<string, string | string[]> | string | null;
  labels: Record<string, string>;
  actions?: ContextSummaryAction[];
  /** Optional warning rendered as an inline alert below the data entries. */
  notice?: string | null;
  /** Shown instead of the entries when `data` is null or empty. */
  emptyText?: string;
  /** Whether the card starts expanded. Defaults to `true`. */
  defaultOpen?: boolean;
}

/**
 * Renders a collapsible context summary card with key-value data entries
 * and optional action buttons.
 */
export function ContextSummaryContent({
  title,
  data,
  labels,
  actions,
  notice,
  emptyText,
  defaultOpen = true,
}: ContextSummaryContentProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Only render entries that have a label mapping
  const entries =
    data && typeof data === 'object'
      ? Object.entries(data).filter(([key]) => key in labels)
      : [];
  const text = typeof data === 'string' ? data.trim() : '';
  const isEmpty = entries.length === 0 && text.length === 0;

  return (
    <div className="mb-4 w-full overflow-hidden rounded-2xl border border-border bg-background">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Header */}
        <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-2 p-4">
          <ListChecks className="size-4 shrink-0 text-foreground" />
          <span className="flex-1 text-left text-base font-medium text-popover-foreground">
            {title}
          </span>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </CollapsibleTrigger>

        {/* Content */}
        <CollapsibleContent>
          {/* Data entries */}
          <div className="px-4">
            <div className="rounded-md bg-muted p-6">
              <div className="flex flex-col gap-4">
                {entries.map(([key, value]) => (
                  <DataEntry key={key} label={labels[key]} value={value} />
                ))}
                {text && <TextEntries text={text} />}
                {isEmpty && emptyText && (
                  <p className="text-base text-muted-foreground">{emptyText}</p>
                )}
              </div>
            </div>
          </div>

          {/* Notice — 8px bottom inset when it closes the card (actions bring
              their own padding otherwise). */}
          {notice && (
            <div className={cn('px-4 pt-4', !actions?.length && 'pb-2')}>
              <NoticeAlert notice={notice} />
            </div>
          )}

          {/* Actions */}
          {actions && actions.length > 0 && (
            <div className="flex items-center justify-end gap-2 p-4">
              {actions.map((action) => (
                <Button
                  key={action.label}
                  variant={action.variant ?? 'default'}
                  size="sm"
                  onClick={action.onClick}
                >
                  {action.icon}
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/**
 * Splits a multi-line text into sections: a line ending with `:` opens a section, `- `/`* `
 * lines are its bullets, any other line is a paragraph of the current section.
 */
export function parseTextSections(
  text: string
): { heading: string | null; lines: string[] }[] {
  const sections: { heading: string | null; lines: string[] }[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.endsWith(':') && !/^[-*•]\s/.test(line)) {
      sections.push({ heading: line.slice(0, -1).trim(), lines: [] });
      continue;
    }
    if (sections.length === 0) sections.push({ heading: null, lines: [] });
    sections[sections.length - 1].lines.push(line.replace(/^[-*•]\s+/, ''));
  }
  return sections.filter((s) => s.lines.length > 0);
}

function TextEntries({ text }: { text: string }) {
  return (
    <>
      {parseTextSections(text).map((section, i) => (
        <div key={`${section.heading ?? ''}-${i}`}>
          {section.heading && (
            <p className="text-base font-medium text-foreground">
              {section.heading}
            </p>
          )}
          <ul className="list-disc pl-5">
            {section.lines.map((line) => (
              <li key={line} className="text-base text-muted-foreground">
                {line}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

function DataEntry({
  label,
  value,
}: {
  label: string;
  value: string | string[];
}) {
  return (
    <div>
      <p className="text-base font-medium text-foreground">{label}</p>
      {Array.isArray(value) ? (
        <ul className="list-disc pl-5">
          {value.map((item) => (
            <li key={item} className="text-base text-muted-foreground">
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-base text-muted-foreground">{value}</p>
      )}
    </div>
  );
}
